import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, FileText, Download, Sun } from "lucide-react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Contrat() {
  const [doc, setDoc] = useState(null);
  const [form, setForm] = useState({ client_name: "", address: "", ean: "", date: "" });

  useEffect(() => {
    api.get("/contract").then(({ data }) => setDoc(data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const download = () => {
    const params = new URLSearchParams(
      Object.fromEntries(Object.entries(form).filter(([, v]) => v))
    ).toString();
    window.open(`${API}/contract/pdf${params ? `?${params}` : ""}`, "_blank");
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-primary px-6 py-6 text-primary-foreground">
        <div className="mx-auto flex max-w-4xl items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent"><Sun className="h-5 w-5" /></span>
            <div>
              <h1 className="font-display text-xl font-bold">Contrat Sparki XL</h1>
              <p className="text-sm text-primary-foreground/70">Document pré-contractuel à envoyer au client</p>
            </div>
          </div>
          <Button asChild variant="secondary" size="sm" className="rounded-full">
            <Link to="/" data-testid="contrat-back-link"><ArrowLeft className="mr-1.5 h-4 w-4" /> Accueil</Link>
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-10">
        <div className="mb-8 rounded-3xl border border-border bg-card p-6" data-testid="contract-personalize">
          <h2 className="mb-1 font-display text-lg font-bold">Personnaliser (optionnel)</h2>
          <p className="mb-5 text-sm text-muted-foreground">
            Renseignez les informations du client pour les inclure dans le PDF, ou laissez vide pour un contrat à compléter à la main.
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label className="mb-1.5 block text-sm">Nom du client</Label>
              <Input data-testid="contract-client-name" value={form.client_name} onChange={(e) => set("client_name", e.target.value)} className="h-11" placeholder="Jean Dupont" />
            </div>
            <div>
              <Label className="mb-1.5 block text-sm">Adresse</Label>
              <Input data-testid="contract-address" value={form.address} onChange={(e) => set("address", e.target.value)} className="h-11" placeholder="Rue…, 1470 Genappe" />
            </div>
            <div>
              <Label className="mb-1.5 block text-sm">Code EAN</Label>
              <Input data-testid="contract-ean" value={form.ean} onChange={(e) => set("ean", e.target.value)} className="h-11" placeholder="54144…" />
            </div>
            <div>
              <Label className="mb-1.5 block text-sm">Date</Label>
              <Input data-testid="contract-date" type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className="h-11" />
            </div>
          </div>
          <Button data-testid="contract-download-button" onClick={download} className="mt-6 h-12 w-full rounded-full bg-accent text-base hover:bg-accent/90 sm:w-auto sm:px-8">
            <Download className="mr-2 h-5 w-5" /> Télécharger le contrat (PDF)
          </Button>
        </div>

        {doc && (
          <motion.article initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="rounded-3xl border border-border bg-card p-8" data-testid="contract-preview">
            <div className="mb-6 flex items-center gap-3 border-b border-border pb-5">
              <FileText className="h-8 w-8 text-accent" />
              <div>
                <h2 className="font-display text-2xl font-bold tracking-tight">{doc.title}</h2>
                <p className="text-sm text-accent">{doc.subtitle}</p>
              </div>
            </div>
            <p className="text-sm leading-relaxed text-muted-foreground">{doc.intro}</p>
            <div className="mt-6 space-y-5">
              {doc.sections.map((s, i) => (
                <div key={i}>
                  <h3 className="font-display text-base font-bold text-primary">{s.title}</h3>
                  {s.body.split("\n").map((line, j) => (
                    <p key={j} className="mt-1 text-sm leading-relaxed text-muted-foreground">{line}</p>
                  ))}
                </div>
              ))}
            </div>
            <p className="mt-8 border-t border-border pt-5 text-xs text-muted-foreground">
              Document pré-contractuel informatif relatif à la formule Sparki XL — présenté par Solaria (Euroconsult SNC, Rue des Chanterelles 13, 1470 Genappe — TVA BE 0644.763.354). Les conditions générales définitives du fournisseur Sparki prévalent en cas de divergence.
            </p>
          </motion.article>
        )}
      </main>
    </div>
  );
}
